/**
 * Ccontainer Component
 * Granular container component with theme-based styling and interactions
 */

'use client';

import { CcontainerRoot } from './CcontainerRoot';
import { useContainerLoadingBorder } from './useContainerLoadingBorder';
import { useContainerMotionModel } from './useContainerMotionModel';
import { useContainerPressInteraction } from './useContainerPressInteraction';
import { useContainerStyleModel } from './useContainerStyleModel';
import type { CcontainerProps } from './Ccontainer.types';

export type { CcontainerProps, CcontainerRootAttributes, CcontainerRootMotionProps } from './Ccontainer.types';

const DEFAULT_CLICK_ANIMATION = false;
const DEFAULT_LOADING = false;

export function Ccontainer(props: CcontainerProps) {
  const { clickAnimation = DEFAULT_CLICK_ANIMATION, loading = DEFAULT_LOADING, onClick } = props;
  const { isHovered, isPressed, interactionHandlers } = useContainerPressInteraction({
    onClick,
    clickAnimation,
  });
  const loadingBorder = useContainerLoadingBorder({ loading });
  const styleModel = useContainerStyleModel({
    props,
    isHovered,
    loadingBorder,
  });
  const motionConfig = useContainerMotionModel({ props, isPressed });

  return (
      <CcontainerRoot
        className={props.className}
        rootAttributes={props.rootAttributes}
        containerStyle={styleModel.containerStyle}
        preserveStyle={props.preserveStyle}
        contentWrapperStyle={styleModel.contentWrapperStyle}
        gradientBorderStyle={styleModel.gradientBorderStyle}
        loadingBorderStyle={styleModel.loadingBorderStyle}
        glowLayer={props.glowLayer}
        hasGlowLayer={styleModel.hasGlowLayer}
        useGradientBorder={styleModel.useGradientBorder}
        isLoadingActive={styleModel.isLoadingActive}
        onClick={onClick}
        interactionHandlers={interactionHandlers}
        rootMotion={props.rootMotion}
        rootRef={props.rootRef}
        useClickMotion={motionConfig.useClickMotion}
        clickMotionProps={motionConfig.clickMotionProps}
        effectiveAnimate={motionConfig.effectiveAnimate}
        effectiveTransition={motionConfig.effectiveTransition}
      >
        {props.children}
      </CcontainerRoot>
  );
}
